import type { HeatmapCell, SessionInsights, TargetMarker } from './tracking-demo.models';

/** One screen's slice of a session's insights, ready to render. */
export interface ScreenView {
  name: string;
  label: string;
  atMs: number;
  clicks: number;
  cells: HeatmapCell[];
  /** The most-clicked elements on this screen, each at its click centroid. */
  markers: TargetMarker[];
}

const SCREEN_LABELS: Record<string, string> = {
  form: 'Instant-demo form',
  lobby: 'Lobby (before join)',
  call: 'Live call',
};

/** Human label for a screen key ("lobby" → "Lobby (before join)"); unknown keys pass through. */
export function screenLabel(name: string): string {
  return SCREEN_LABELS[name] ?? name;
}

/**
 * Split a session's click points by screen: each screen gets its own heatmap cells and
 * the top clicked elements with where they sit (centroid of their clicks). Pure — no
 * Angular, no DOM. Older rows (two-value points, no `screens`) land on one "call" screen.
 */
export function screenViews(insights: SessionInsights | null | undefined, topN = 6): ScreenView[] {
  if (!insights) return [];
  const screens = insights.screens?.length ? insights.screens : [{ name: 'call', atMs: 0 }];
  const buckets = screens.map((s) => ({
    name: s.name,
    atMs: s.atMs,
    clicks: 0,
    cells: [] as HeatmapCell[],
    tallies: new Map<string, { count: number; sx: number; sy: number }>(),
  }));

  for (const point of insights.clickPoints ?? []) {
    const [x, y, screenIdx = 0, targetIdx] = point;
    const bucket = buckets[screenIdx] ?? buckets[0];
    if (!bucket || typeof x !== 'number' || typeof y !== 'number') continue;
    bucket.clicks++;
    bucket.cells.push([x, y, 1]);
    const label = targetIdx === undefined ? undefined : insights.targets?.[targetIdx];
    if (!label) continue;
    const tally = bucket.tallies.get(label) ?? { count: 0, sx: 0, sy: 0 };
    tally.count++;
    tally.sx += x;
    tally.sy += y;
    bucket.tallies.set(label, tally);
  }

  return buckets.map((b) => ({
    name: b.name,
    label: screenLabel(b.name),
    atMs: b.atMs,
    clicks: b.clicks,
    cells: b.cells,
    markers: [...b.tallies]
      .map(([label, t]) => ({ label, count: t.count, x: Math.round(t.sx / t.count), y: Math.round(t.sy / t.count) }))
      .sort((a, c) => c.count - a.count)
      .slice(0, topN),
  }));
}
